import { RefObject, useEffect } from 'react';

import { JDSMenuBlockProps } from './generated';
import { LinkProps } from '../jdslink/logic';
import { setPositions } from './positions';
import { useEscapeClose } from '../../hooks/useEscapeClose';
import { useRootRef } from '../../hooks/useRootRef';
import { useScrollClose } from '../../hooks/useScrollClose';

export interface MenuBlockItemProps {
  /**
   * Type of the List Item. type 'entry' corresponds the selectable option item, type 'divider' corresponds to the dividing line between option items and type 'heading' corresponds to the heading given to and option or set of option Items.
   * (Optional)
   *  The default value is 'entry'.
   */
  type?: 'entry' | 'divider' | 'heading';
  /**
   * The visible text in the Option Item. If the type is set to `heading` this prop is used to show the heading.
   */
  label?: string;
  /**
   * The value of the option Item.
   * (Optional)
   *  If this prop is not provided, the `label` prop is used as the value.
   */
  value?: string;
  /**
   * This prop denotes whether the option item is disabled or not. valid only for type 'entry'
   * (Optional)
   * The default value is false.
   */
  disabled?: boolean;
  /**
   * Add link related props to make the items act as links
   */
  linkConfig?: LinkProps;
  prefix?: string | JSX.Element;
  suffix?: string | JSX.Element;
}

export interface MenuBlockProps
  extends Omit<
    JDSMenuBlockProps,
    'onSelect' | 'onCLose' | 'onAppear' | 'items' | 'onClick'
  > {
  /**
   * The Option Items. Check `MenuBlockItemProps` type for more details
   */
  items: MenuBlockItemProps[];
  /**
   * The target element underneath which the menu should open from.
   */
  anchorEl?: RefObject<HTMLElement>;
  /**
   * Triggers when an Item / entry is selected.
   * @param index : index of the selected item
   * @param value : value of the selected item
   */
  onSelect?: (index: number, value: string) => void;
  /**
   * Triggers when the MenuBlock is opened.
   */
  onOpen?: () => void;
  /**
   * Triggers when the MenuBlock is closed
   */
  onClose?: () => void;
  open?: boolean;
}
export interface MenuBlockInternalProps extends MenuBlockProps {
  offsetX?: number;
  offsetY?: number;
  maxWidth?: number;
}
const MENUBLOCK_SELECTOR = 'div#container.j-jds-menu-block-container';

export const withLogic =
  (Component: React.ComponentType<MenuBlockProps>): React.FC<MenuBlockProps> =>
  (props: MenuBlockProps) => {
    const { element, rootRef } = useRootRef();

    const internalProps: MenuBlockInternalProps = props;

    const { items, anchorEl, open } = props;

    const menublockRef =
      element?.querySelector<HTMLDivElement>(MENUBLOCK_SELECTOR);

    useEscapeClose(props.onClose, true);
    useScrollClose(props.onClose);

    useEffect(() => {
      if (open) {
        props.onOpen?.();
      }
    }, [open]);

    if (!items?.length) return null;

    const { left, top, height, width } = setPositions(
      anchorEl?.current,
      menublockRef,
      String(props.maxHeight)
    );
    return (
      <Component
        {...{
          ...props,
          open,
          rootRef,
          items,
          offsetX: internalProps.offsetX || left,
          maxHeight: !open ? 0 : props.maxHeight || height,
          offsetY: internalProps.offsetY || top,
          _hasIcon: items.some((i) => Boolean(i.prefix)),
          onSelect: (index: number) => {
            props.onSelect?.(index, items?.[index].value || '');
            props.onClose?.();
          },
          onClick: (ev: React.MouseEvent<HTMLDivElement>) => {
            ev.stopPropagation();
            props.onClick?.(ev);
          },
          minWidth: internalProps.maxWidth || Math.max(width || 0, 250)
        }}
      />
    );
  };
